// 16-direction compass rose — interactive (edit mode) or read-only (detail view).
// M53-C revision: discrete segment ring, bearing arrow, center readout, explicit clear button.

import { useEffect, useRef, useState, type JSX } from 'react';

import { useCircularGesture } from '@/hooks/use-circular-gesture';

import {
  DIRECTIONS,
  DIR_DEGREES,
  FULL_NAMES,
  bearingToNearestDirection,
} from './wind-compass-rose-utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type DirCategory = 'cardinal' | 'intercardinal' | 'subcardinal';

function dirCategory(dir: Direction16): DirCategory {
  if (dir === 'N' || dir === 'E' || dir === 'S' || dir === 'W') return 'cardinal';
  if (dir === 'NE' || dir === 'SE' || dir === 'SW' || dir === 'NW') return 'intercardinal';
  return 'subcardinal';
}

/** Compass-order sort comparator (sorts by index in DIRECTIONS). */
function compassOrder(a: string, b: string): number {
  return DIRECTIONS.indexOf(a as Direction16) - DIRECTIONS.indexOf(b as Direction16);
}

/**
 * Convert a compass bearing (0=North, CW) to SVG x/y relative to a center.
 * SVG y increases downward, so North (up visual) needs negative dy.
 */
function bearingToXY(bearingDeg: number, radius: number): { x: number; y: number } {
  const rad = (bearingDeg * Math.PI) / 180;
  return {
    x: Math.sin(rad) * radius,
    y: -Math.cos(rad) * radius,
  };
}

/**
 * Build an SVG arc path string for a single discrete segment.
 * Each segment spans GAP_DEG on each side of its center bearing (21° wide, 1.5° gap each side).
 * The segment is a ring annulus slice (not pie/wedge) — from innerR to outerR.
 */
const SEGMENT_HALF_ANGLE = 10.5; // degrees (21° / 2)

function buildSegmentPath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  centerBearing: number,
): string {
  const startBearing = centerBearing - SEGMENT_HALF_ANGLE;
  const endBearing = centerBearing + SEGMENT_HALF_ANGLE;

  // Outer arc: start → end (clockwise)
  const outerStart = bearingToXY(startBearing, outerR);
  const outerEnd = bearingToXY(endBearing, outerR);
  // Inner arc: end → start (counter-clockwise, i.e. sweep-flag=0)
  const innerEnd = bearingToXY(endBearing, innerR);
  const innerStart = bearingToXY(startBearing, innerR);

  const osx = cx + outerStart.x;
  const osy = cy + outerStart.y;
  const oex = cx + outerEnd.x;
  const oey = cy + outerEnd.y;
  const iex = cx + innerEnd.x;
  const iey = cy + innerEnd.y;
  const isx = cx + innerStart.x;
  const isy = cy + innerStart.y;

  return [
    `M ${osx} ${osy}`,
    `A ${outerR} ${outerR} 0 0 1 ${oex} ${oey}`,
    `L ${iex} ${iey}`,
    `A ${innerR} ${innerR} 0 0 0 ${isx} ${isy}`,
    'Z',
  ].join(' ');
}

/**
 * Compute all directions covered by a sweep arc (inclusive of start and end).
 * `startBearing` and `endBearing` are snapped 22.5° bearings.
 * `clockwise` determines the sweep direction.
 */
function directionsInSweep(
  startBearing: number,
  endBearing: number,
  clockwise: boolean,
): Direction16[] {
  const startIdx = DIRECTIONS.findIndex((d) => Math.abs(DIR_DEGREES[d] - startBearing) < 1);
  const endIdx = DIRECTIONS.findIndex((d) => Math.abs(DIR_DEGREES[d] - endBearing) < 1);

  if (startIdx === -1 || endIdx === -1) return [];
  if (startIdx === endIdx) return [DIRECTIONS[startIdx]];

  const result: Direction16[] = [];
  let i = startIdx;
  while (i !== endIdx) {
    result.push(DIRECTIONS[i]);
    i = clockwise ? (i + 1) % 16 : (i + 15) % 16;
  }
  result.push(DIRECTIONS[endIdx]);
  return result;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WindCompassRoseProps {
  selectedDirections: string[];
  onChange: (directions: string[]) => void;
  /** Existing backward-compat prop — direction string like "NW". */
  currentWindDirection?: string;
  /** New: takes precedence over currentWindDirection. Bearing in degrees (0–359). */
  currentWindBearing?: number | null;
  /** Current wind speed in mph/kph for center readout display. */
  currentWindSpeed?: number | null;
  readOnly?: boolean;
  hideLabel?: boolean;
  /** When true, suppresses all interaction (not just onChange). */
  disabled?: boolean;
  /** SVG viewBox edge in px. Default 300. */
  size?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WindCompassRose({
  selectedDirections,
  onChange,
  currentWindDirection,
  currentWindBearing,
  currentWindSpeed,
  readOnly = false,
  hideLabel = false,
  disabled = false,
  size = 300,
}: WindCompassRoseProps) {
  const isInteractive = !readOnly && !disabled;

  // ── Sizing ──────────────────────────────────────────────────────────────
  const cx = size / 2;
  const cy = size / 2;
  const compassRadius = size * 0.4;       // radius to tick ring
  // Segment ring sits between 70–85% of compass radius
  const segmentOuterR = compassRadius * 0.85;
  const segmentInnerR = compassRadius * 0.70;

  // ── Animation state ──────────────────────────────────────────────────────
  const [justLockedIn, setJustLockedIn] = useState(false);
  const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Keyboard focus state ─────────────────────────────────────────────────
  const [focusedDirIdx, setFocusedDirIdx] = useState<number>(-1);

  // ── Gesture hook ─────────────────────────────────────────────────────────
  const { isDragging, handlers: gestureHandlers } = useCircularGesture({
    centerX: cx,
    centerY: cy,
    snapDegrees: 22.5,
    disabled: !isInteractive,
    onSnapCrossed: (_snapped) => {
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(8);
      }
    },
    onDragEnd: (finalBearing, sweepArc) => {
      const newPoints = directionsInSweep(
        sweepArc.startDeg,
        sweepArc.endDeg,
        sweepArc.clockwise,
      );
      if (newPoints.length === 0) return;

      // ADD semantics: merge with existing, never replace.
      const merged = Array.from(new Set([...selectedDirections, ...newPoints])).sort(compassOrder);
      onChange(merged);

      // Lock-in pulse: set true, clear after 300ms.
      setJustLockedIn(true);
      if (lockTimerRef.current !== null) clearTimeout(lockTimerRef.current);
      lockTimerRef.current = setTimeout(() => {
        setJustLockedIn(false);
        lockTimerRef.current = null;
      }, 300);
    },
  });

  // Cleanup lock timer on unmount.
  useEffect(() => {
    return () => {
      if (lockTimerRef.current !== null) clearTimeout(lockTimerRef.current);
    };
  }, []);

  // ── Current wind bearing resolution ──────────────────────────────────────
  const resolvedBearing: number | null =
    currentWindBearing != null
      ? currentWindBearing
      : currentWindDirection
        ? (DIR_DEGREES[currentWindDirection as Direction16] ?? null)
        : null;

  const favorability: 'favorable' | 'unfavorable' | 'unknown' =
    resolvedBearing == null
      ? 'unknown'
      : selectedDirections.includes(bearingToNearestDirection(resolvedBearing))
        ? 'favorable'
        : 'unfavorable';

  // ── Tap handler on direction nodes ───────────────────────────────────────
  function handleDirTap(dir: Direction16) {
    if (!isInteractive) return;
    if (selectedDirections.includes(dir)) {
      onChange(selectedDirections.filter((d) => d !== dir));
    } else {
      onChange([...selectedDirections, dir].sort(compassOrder));
    }
  }

  // ── Keyboard navigation ───────────────────────────────────────────────────
  function handleKeyDown(e: React.KeyboardEvent<SVGSVGElement>) {
    if (!isInteractive) return;
    if (focusedDirIdx < 0) return;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        e.preventDefault();
        setFocusedDirIdx((focusedDirIdx + 1) % 16);
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        e.preventDefault();
        setFocusedDirIdx((focusedDirIdx + 15) % 16);
        break;
      }
      case ' ':
      case 'Enter': {
        e.preventDefault();
        handleDirTap(DIRECTIONS[focusedDirIdx]);
        break;
      }
      default:
        break;
    }
  }

  // ── Debounced live-region text ────────────────────────────────────────────
  const [liveText, setLiveText] = useState('');
  const liveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function updateLiveRegion(dirs: string[]) {
    if (liveTimerRef.current !== null) clearTimeout(liveTimerRef.current);
    liveTimerRef.current = setTimeout(() => {
      const text =
        dirs.length === 0
          ? 'No ideal wind directions selected'
          : `Selected ideal wind directions: ${dirs.map((d) => FULL_NAMES[d as Direction16] ?? d).join(', ')}`;
      setLiveText(text);
      liveTimerRef.current = null;
    }, 150);
  }

  useEffect(() => {
    return () => {
      if (liveTimerRef.current !== null) clearTimeout(liveTimerRef.current);
    };
  }, []);

  function handleDirTapWithLive(dir: Direction16) {
    if (!isInteractive) return;
    const next = selectedDirections.includes(dir)
      ? selectedDirections.filter((d) => d !== dir)
      : [...selectedDirections, dir].sort(compassOrder);
    onChange(next);
    updateLiveRegion(next);
  }

  // ── Center readout ─────────────────────────────────────────────────────
  const hasWeatherData = resolvedBearing != null && currentWindSpeed != null;
  const centerReadoutDir = resolvedBearing != null
    ? bearingToNearestDirection(resolvedBearing)
    : null;
  // SAFETY: currentWindSpeed is non-null here because hasWeatherData guards both
  // resolvedBearing and currentWindSpeed before this expression is reached.
  const centerText = hasWeatherData && centerReadoutDir != null
    ? `${centerReadoutDir} ${Math.round(currentWindSpeed!)}`
    : null;

  // ── Bearing arrow (triangle) at outer rim ───────────────────────────────
  // Triangle points inward toward center. Tip at innermost point, base at outer rim.
  function buildBearingArrow(bearing: number): JSX.Element {
    // SAFETY: JSX imported from react via `type JSX`
    const arrowR = compassRadius + 20; // outer perimeter position
    const tipPt = bearingToXY(bearing, arrowR - 10); // tip points inward
    const basePt = bearingToXY(bearing, arrowR);      // base on outer rim
    // Perpendicular points for the base of the triangle
    const perpPt = bearingToXY(bearing + 90, 5);

    const tipX = cx + tipPt.x;
    const tipY = cy + tipPt.y;
    const b1x = cx + basePt.x - perpPt.x;
    const b1y = cy + basePt.y - perpPt.y;
    const b2x = cx + basePt.x + perpPt.x;
    const b2y = cy + basePt.y + perpPt.y;

    return (
      <polygon
        points={`${tipX},${tipY} ${b1x},${b1y} ${b2x},${b2y}`}
        fill="var(--color-accent)"
        opacity="0.9"
        data-testid="bearing-arrow"
      />
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="wind-compass-rose"
      data-selected-count={String(selectedDirections.length)}
      data-is-dragging={String(isDragging)}
      data-disabled={String(disabled)}
      data-current-wind-bearing={resolvedBearing != null ? String(Math.round(resolvedBearing)) : ''}
      data-favorability={favorability}
      data-just-locked-in={String(justLockedIn)}
    >
      {!hideLabel && (
        <p className="field-label">
          {readOnly ? 'Wind Conditions' : 'Ideal Wind Directions'}
        </p>
      )}

      {/* ARIA live region for direction announcements */}
      <span role="status" aria-live="polite" className="sr-only">
        {liveText}
      </span>

      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-label="Ideal wind direction selector"
        role="group"
        className="mx-auto"
        tabIndex={isInteractive ? 0 : -1}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (focusedDirIdx < 0) setFocusedDirIdx(0); }}
        style={{ userSelect: 'none', touchAction: 'none' }}
        {...(isInteractive ? gestureHandlers : {})}
      >
        {/* Outer hairline ring */}
        <circle
          cx={cx}
          cy={cy}
          r={compassRadius}
          fill="none"
          style={{ stroke: 'var(--color-accent-a20)' }}
          strokeWidth="0.5"
        />

        {/* 16 discrete arc segments — selected state drives fill/stroke */}
        <g className={justLockedIn ? 'compass-lock-pulse' : ''}>
          {DIRECTIONS.map((dir) => {
            const bearing = DIR_DEGREES[dir];
            const isSelected = selectedDirections.includes(dir);
            const segPath = buildSegmentPath(cx, cy, segmentInnerR, segmentOuterR, bearing);

            if (isSelected) {
              return (
                <path
                  key={`seg-${dir}`}
                  data-testid="selected-arc"
                  data-segment-dir={dir}
                  d={segPath}
                  fill="var(--color-accent)"
                  fillOpacity="0.8"
                  stroke="var(--color-accent)"
                  strokeWidth="0.5"
                  filter="url(#segment-glow)"
                />
              );
            }
            return (
              <path
                key={`seg-${dir}`}
                data-segment-dir={dir}
                d={segPath}
                fill="transparent"
                stroke="var(--color-border)"
                strokeWidth="0.5"
              />
            );
          })}

          {/* Glow filter for selected segments */}
          <defs>
            <filter id="segment-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>
        </g>

        {/* 16 direction tick marks */}
        {DIRECTIONS.map((dir) => {
          const deg = DIR_DEGREES[dir];
          const cat = dirCategory(dir);
          const tickLen = cat === 'cardinal' ? 8 : cat === 'intercardinal' ? 6 : 4;
          const strokeW = cat === 'cardinal' ? 1 : cat === 'intercardinal' ? 0.75 : 0.5;
          const fontSize = cat === 'cardinal' ? 10 : cat === 'intercardinal' ? 9 : 0;
          const showLabel = fontSize > 0;

          // Tick endpoints: from compassRadius inward by tickLen
          const outerPt = bearingToXY(deg, compassRadius);
          const innerPt = bearingToXY(deg, compassRadius - tickLen);
          const labelPt = bearingToXY(deg, compassRadius + 14);

          const selected = selectedDirections.includes(dir);
          const isFocused = isInteractive && focusedDirIdx >= 0 && DIRECTIONS[focusedDirIdx] === dir;

          // Node position on direction ring for tap target
          const nodePt = bearingToXY(deg, compassRadius * 0.9);
          const nodeR = cat === 'cardinal' ? 14 : cat === 'intercardinal' ? 12 : 10;

          return (
            <g
              key={dir}
              data-testid={`wind-dir-${dir}`}
              data-dir-selected={String(selected)}
              role="button"
              aria-pressed={selected}
              aria-label={FULL_NAMES[dir]}
              onClick={() => handleDirTapWithLive(dir)}
              onFocus={() => setFocusedDirIdx(DIRECTIONS.indexOf(dir))}
              tabIndex={-1}
              style={{ cursor: isInteractive ? 'pointer' : 'default', outline: 'none' }}
            >
              {/* Invisible larger hit area */}
              <circle
                cx={cx + nodePt.x}
                cy={cy + nodePt.y}
                r={nodeR}
                style={{
                  fill: selected ? 'var(--color-accent-a35)' : 'transparent',
                  stroke: selected ? 'var(--color-accent-a60)' : 'transparent',
                }}
                strokeWidth={selected ? 1.5 : 0}
              />

              {/* Focus ring */}
              {isFocused && (
                <circle
                  cx={cx + nodePt.x}
                  cy={cy + nodePt.y}
                  r={nodeR + 2}
                  fill="none"
                  stroke="var(--color-focus-ring)"
                  strokeWidth="1.5"
                />
              )}

              {/* Tick mark */}
              <line
                x1={cx + outerPt.x}
                y1={cy + outerPt.y}
                x2={cx + innerPt.x}
                y2={cy + innerPt.y}
                stroke={selected ? 'var(--color-accent)' : 'var(--color-bark-400)'}
                strokeWidth={strokeW}
                strokeLinecap="round"
              />

              {/* Label */}
              {showLabel && (
                <text
                  x={cx + labelPt.x}
                  y={cy + labelPt.y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={fontSize}
                  fontFamily="'DM Sans', system-ui, sans-serif"
                  fill={
                    cat === 'cardinal'
                      ? 'var(--color-bark-300)'
                      : 'var(--color-bark-400)'
                  }
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {dir}
                </text>
              )}
            </g>
          );
        })}

        {/* Center: wind readout text (when weather data available) or small dot */}
        {centerText != null ? (
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={12}
            fontWeight="bold"
            fontFamily="'DM Sans', system-ui, sans-serif"
            fill={
              favorability === 'favorable'
                ? 'var(--color-favorable, #4a8c5c)'
                : 'var(--color-bark-100)'
            }
            style={{ pointerEvents: 'none', userSelect: 'none' }}
            data-testid="wind-center-readout"
            data-favorable={String(favorability === 'favorable')}
          >
            {centerText}
          </text>
        ) : (
          <circle
            cx={cx}
            cy={cy}
            r={2}
            fill="var(--color-bark-500)"
            data-testid="wind-center-dot"
          />
        )}

        {/* Current wind bearing arrow — triangle on outer rim pointing inward */}
        {resolvedBearing != null && (
          <g
            data-testid="current-wind-indicator"
            data-favorability={favorability}
            data-wind-bearing={String(Math.round(resolvedBearing))}
          >
            {buildBearingArrow(resolvedBearing)}
          </g>
        )}
      </svg>

      {/* CLEAR SELECTION button — only visible when selections exist */}
      {isInteractive && selectedDirections.length > 0 && (
        <button
          type="button"
          data-testid="clear-selection-button"
          onClick={() => onChange([])}
          className="field-label mx-auto block cursor-pointer rounded px-2 py-1 text-[var(--color-bark-400)] border border-[var(--color-bark-600)] bg-transparent hover:border-[var(--color-bark-400)]"
        >
          CLEAR SELECTION
        </button>
      )}

      {!readOnly && isInteractive && (
        <p className="text-body-xs text-[var(--color-text-muted)] text-center">
          Drag to sweep multiple directions
        </p>
      )}
    </div>
  );
}
